/**
 * Автовыбор (DESIGN §4.6): нейтральная политика для таймаута, автопилота
 * и ботов. Фокус — самое обычное занятие возраста, вариант решения —
 * `timeoutOption` (наиболее вероятный для такого человека, его считает
 * таблица решений), а если он недоступен — первый доступный.
 *
 * `expectedPolicy` (core-loop-v2 §3) тратит энергию года так, как это
 * делает «обычный» человек: при деньгах < POLICY_POOR_GDP·ВВП и работе —
 * смена (`work.shift`), при здоровье < POLICY_SICK_HEALTH — зал (`gym`),
 * иначе отдых (`rest`, +1 счастья за очко). Азарта нет никогда. Числа
 * исходов (POLICY_*) — те же, что у игрока в таблице мест
 * (@life/minigames/places.ts берёт их отсюда: движок не может импортировать
 * пакет мест), они малы, и калибровка этапа 1 не плывёт; убывающая отдача
 * учитывается (в одно место не больше VISITS_MAX раз, дальше — отдых).
 *
 * Чистые функции без случайности.
 */
import { getValue, type Dataset } from '@life/data';
import { VISITS_MAX, type ActionInput } from './energy';
import type { MinigameOutcome } from './minigames';
import type { Decision, Focus, Life } from './types';

/** До этого возраста — «друзья и игры». */
const PLAY_MAX_AGE = 6;
/** С этого возраста слабое здоровье переводит фокус на «здоровье». */
const HEALTH_FOCUS_MIN_AGE = 30;
const HEALTH_FOCUS_THRESHOLD = 40;
/** До этого возраста работающий человек выбирает «работу». */
const WORK_FOCUS_MAX_AGE = 60;

/** Деньги меньше этой доли ВВП на душу при работе — автопилот берёт смену. */
export const POLICY_POOR_GDP = 0.1;
/** Здоровье ниже порога — автопилот идёт в зал. */
export const POLICY_SICK_HEALTH = 50;
/** Смена: доля годового дохода за визит (план 2d: +0,04·доход). */
export const POLICY_SHIFT_INCOME_SHARE = 0.04;
/** Смена: здоровье за визит (усталость). */
export const POLICY_SHIFT_HEALTH = -1;
/** Зал: здоровье и счастье за визит (interactives.md H2). */
export const POLICY_GYM_HEALTH = 2;
export const POLICY_GYM_HAPPINESS = 1;
/** Отдых: счастье за очко (core-loop-v2 §2). */
export const POLICY_REST_HAPPINESS = 1;

export const PLACE_WORK = 'work';
export const PLACE_GYM = 'gym';
export const PLACE_REST = 'rest';

/**
 * Фокус года по умолчанию: 'play' до 6; 'study', пока учится;
 * 'health', если здоровье < 40 и возраст ≥ 30; 'work' при работе до 60;
 * иначе 'balance'.
 */
export function chooseFocus(life: Life): Focus {
  if (life.age < PLAY_MAX_AGE) return 'play';
  if (life.inSchool) return 'study';
  if (life.stats.health < HEALTH_FOCUS_THRESHOLD && life.age >= HEALTH_FOCUS_MIN_AGE) return 'health';
  if (life.job !== null && life.age < WORK_FOCUS_MAX_AGE) return 'work';
  return 'balance';
}

/** Вариант решения по умолчанию: `timeoutOption`, если доступен, иначе первый доступный. */
export function chooseOption(_life: Life, decision: Decision): string {
  const timeout = decision.options.find((o) => o.id === decision.timeoutOption);
  if (timeout?.available) return timeout.id;
  const first = decision.options.find((o) => o.available);
  if (!first) throw new RangeError(`chooseOption: decision ${decision.id} has no available option`);
  return first.id;
}

function shiftOutcome(life: Life): MinigameOutcome {
  return { moneyDelta: Math.max(0, (life.job?.income ?? 0) * POLICY_SHIFT_INCOME_SHARE), stats: { health: POLICY_SHIFT_HEALTH } };
}

const GYM_OUTCOME: MinigameOutcome = { stats: { health: POLICY_GYM_HEALTH, happiness: POLICY_GYM_HAPPINESS } };
const REST_OUTCOME: MinigameOutcome = { stats: { happiness: POLICY_REST_HAPPINESS } };

/** Предпочтения по порядку: что хочется сделать в этом году. */
function preferences(life: Life, ds: Dataset): ActionInput[] {
  const out: ActionInput[] = [];
  const gdp = Math.max(0, getValue(ds, life.iso3, 'gdpPpp', life.birthYear + life.age).v);
  if (life.job !== null && life.money < POLICY_POOR_GDP * gdp) out.push({ place: PLACE_WORK, action: 'shift', outcome: shiftOutcome(life) });
  if (life.stats.health < POLICY_SICK_HEALTH) out.push({ place: PLACE_GYM, action: 'train', outcome: GYM_OUTCOME });
  out.push({ place: PLACE_REST, action: 'rest', outcome: REST_OUTCOME });
  return out;
}

/**
 * Действия автопилота на год: по очку энергии на действие, предпочтения по
 * порядку с учётом уже сделанных визитов (не больше VISITS_MAX в место).
 * Детерминировано, без азартных мест; никогда не возвращает действия,
 * которые `applyActions` отбросит.
 */
export function expectedPolicy(life: Life, ds: Dataset): ActionInput[] {
  const prefs = preferences(life, ds);
  const visits: Record<string, number> = { ...life.visits };
  const actions: ActionInput[] = [];
  for (let left = Math.max(0, Math.trunc(life.energy)); left > 0; left -= 1) {
    const pick = prefs.find((p) => (visits[p.place] ?? 0) < VISITS_MAX);
    if (!pick) break;
    actions.push(pick);
    visits[pick.place] = (visits[pick.place] ?? 0) + 1;
  }
  return actions;
}
